/* eslint-disable */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import MemberAvatar from "../components/MemberAvatar";
import MemberBadge from "../components/MemberBadge";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import HoursLocationLink from "../components/HoursLocationLink";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function MemberProfile() {
  const { memberId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messaging, setMessaging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/community/members/${memberId}`)
      .then((r) => { if (!cancelled) setProfile(r.data); })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Member not found");
        nav("/community");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [memberId, nav]);

  const openMessageThread = async () => {
    // Phase 2 will wire this to real DM endpoints. For now we mark intent
    // and show a friendly "coming soon" toast if the backend hasn't shipped
    // the DM router yet.
    setMessaging(true);
    try {
      const r = await api.post("/community/messages/threads", { with_user_id: memberId });
      const threadId = r.data?.thread_id;
      if (threadId) {
        nav(`/community/messages/${threadId}`);
      } else {
        toast.error("Could not open conversation");
      }
    } catch (e) {
      if (e?.response?.status === 404 && String(e?.response?.data?.detail || "").includes("thread")) {
        toast.error(e.response.data.detail);
      } else if (e?.response?.status === 404) {
        // DM router not shipped yet — friendly message.
        toast.info("Direct messages ship in the next update — we'll notify you.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not open conversation");
      }
    } finally {
      setMessaging(false);
    }
  };

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <section className="m-container" data-testid="member-profile">
        <Link to="/community" className="m-btn-ghost" style={{ display: "inline-block", marginBottom: 20 }}>
          ← Back to Community
        </Link>

        {loading && <div className="m-loading-inline">Loading profile…</div>}

        {!loading && profile && (
          <>
            <div className="m-card" style={{ padding: 32, marginBottom: 24 }}>
              <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                <MemberAvatar user={profile} size={120} clickable={false} />
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <h1 className="rec-h2" style={{ margin: 0 }} data-testid="member-name">
                      {profile.display_name || profile.name}
                    </h1>
                    {/* Session 60.7 · Profile cleanup — a single MemberBadge
                        near the identity is the only visible status. The
                        duplicate "Founding member" mini chip and the
                        admin "Team" gold block have been removed. Official
                        accounts render no badge via is_official_account. */}
                    <MemberBadge
                      member_type={profile.member_type}
                      founding_member={profile.founding_member}
                      is_official_account={profile.is_official_account}
                      size="sm"
                    />
                  </div>
                  {profile.profession && (
                    <p className="m-muted" style={{ margin: "6px 0 4px" }} data-testid="member-profession">
                      {profile.profession}
                      {profile.company ? ` · ${profile.company}` : ""}
                    </p>
                  )}
                  {(profile.city || profile.state) && (
                    <p className="m-muted small" style={{ margin: 0 }} data-testid="member-location">
                      📍 {[profile.city, profile.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {profile.bio && (
                    <p style={{ marginTop: 14, whiteSpace: "pre-wrap", lineHeight: 1.55 }} data-testid="member-bio">
                      {profile.bio}
                    </p>
                  )}
                  {(profile.linkedin || profile.website) && (
                    <div style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap" }}>
                      {profile.linkedin && (
                        <a href={profile.linkedin} target="_blank" rel="noopener noreferrer"
                           className="m-muted small" style={{ color: "var(--gold)" }}
                           data-testid="member-linkedin">LinkedIn ↗</a>
                      )}
                      {profile.website && (
                        <a href={profile.website} target="_blank" rel="noopener noreferrer"
                           className="m-muted small" style={{ color: "var(--gold)" }}
                           data-testid="member-website">Website ↗</a>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    {profile.is_self ? (
                      <Link to="/profile" className="m-btn-gold" data-testid="member-edit-my-profile">
                        Edit my profile
                      </Link>
                    ) : profile.can_message ? (
                      <button
                        className="m-btn-gold"
                        onClick={openMessageThread}
                        disabled={messaging}
                        data-testid="member-message-btn"
                      >{messaging ? "Opening…" : "Message"}</button>
                    ) : (
                      <span className="m-muted small" data-testid="member-dms-closed">
                        {profile.is_self ? "" : "Messages closed"}
                      </span>
                    )}
                    <span className="m-muted small">
                      {profile.post_count || 0} post{profile.post_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="m-side-title" style={{ marginTop: 20 }}>
              Recent posts
            </h3>
            {(!profile.recent_posts || profile.recent_posts.length === 0) ? (
              <div className="m-empty small" data-testid="member-no-posts">
                {profile.display_name || profile.name} hasn't posted yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {profile.recent_posts.map((p) => (
                  <article key={p.post_id} className="m-card"
                           data-testid={`member-post-${p.post_id}`}
                           style={{ padding: 16 }}>
                    <div className="m-muted small" style={{ marginBottom: 6 }}>
                      {timeAgo(p.created_at)}
                      {p.scope && ` · ${p.scope}`}
                    </div>
                    {p.hours_location && (
                      <div
                        data-testid={`member-post-hours-${p.post_id}`}
                        style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}
                      >
                        <span className="m-muted small">Worked from</span>
                        <HoursLocationLink
                          cafeId={p.hours_location.cafe_id}
                          name={p.hours_location.name}
                          testid={`member-post-hours-link-${p.post_id}`}
                        />
                        {(p.hours_location.city || p.hours_location.state) && (
                          <span className="m-muted small">
                            {[p.hours_location.city, p.hours_location.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                    <p style={{ whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.5 }}>{p.body}</p>
                    <div className="m-muted small" style={{ marginTop: 10, display: "flex", gap: 14 }}>
                      <span>♥ {p.likes_count || 0}</span>
                      <span>💬 {p.comments_count || 0}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* Session 60.25 · Public "Places I've worked" — backend
                honors profile privacy (`discoverable_in_directory=false`
                returns an empty list). Section hidden entirely when the
                list is empty so private profiles look tidy. */}
            {Array.isArray(profile.recent_check_ins) && profile.recent_check_ins.length > 0 && (
              <>
                <h3 className="m-side-title" style={{ marginTop: 24 }}>
                  Places I&apos;ve worked
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="member-checkins-list">
                  {profile.recent_check_ins.map((ci) => {
                    const cafe = ci.cafe || {};
                    const cityLine = [cafe.city, cafe.state].filter(Boolean).join(", ");
                    const when = ci.checked_in_at
                      ? new Date(ci.checked_in_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                      : "";
                    return (
                      <div
                        key={ci.check_in_id}
                        className="m-card"
                        data-testid={`member-checkin-${ci.check_in_id}`}
                        style={{ padding: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
                      >
                        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                          <div style={{ marginBottom: 6 }}>
                            <HoursLocationLink
                              cafeId={cafe.cafe_id}
                              name={cafe.name || "Métier Hours location"}
                              testid={`member-checkin-cafe-${ci.check_in_id}`}
                            />
                          </div>
                          <div className="m-muted small" style={{ marginTop: 2 }}>
                            {cityLine || "Métier Hours"}
                            <span style={{ opacity: 0.55 }}> · {when}</span>
                            {ci.work_intent_label && (
                              <span style={{ marginLeft: 8, color: "var(--gold, #B8913A)", fontWeight: 600 }}>
                                · 🎯 {ci.work_intent_label}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </AppShell>
  );
}
